<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      <TransitionGroup name="notification" tag="div">
        <div v-for="notification in notifications" :key="notification.id" :class="[
          'p-4 mb-2 rounded-lg shadow-lg border border-l-4 bg-white',
          getNotificationClasses(notification.type)
        ]">
          <!-- Header -->
          <div class="flex items-start justify-between">
            <div class="flex items-start space-x-3">
              <!-- Icon -->
              <div class="flex-shrink-0">
                <component :is="getIcon(notification.type)" :class="getIconClasses(notification.type)" />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h4 :class="['text-sm font-medium', getTitleClasses(notification.type)]">
                  {{ notification.title }}
                </h4>
                <p v-if="notification.message" :class="['mt-1 text-xs', getMessageClasses(notification.type)]">
                  {{ notification.message }}
                </p>
              </div>
            </div>

            <!-- Close button -->
            <button @click="removeNotification(notification.id)"
              class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd" />
              </svg>
            </button>
          </div>

          <!-- Actions -->
          <div v-if="notification.actions && notification.actions.length > 0" class="mt-3 flex space-x-2">
            <button v-for="action in notification.actions" :key="action.label"
              @click="action.action(); removeNotification(notification.id)" :class="[
                'px-3 py-1 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                action.variant === 'primary'
                  ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500'
              ]">
              {{ action.label }}
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useNotifications } from '@/composables/useNotifications';

const { notifications, removeNotification } = useNotifications();

// Icons for different notification types
const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return 'CheckCircleIcon';
    case 'error':
      return 'XCircleIcon';
    case 'warning':
      return 'ExclamationTriangleIcon';
    case 'info':
    default:
      return 'InformationCircleIcon';
  }
};

const getNotificationClasses = (type: string) => {
  switch (type) {
    case 'success':
      return 'border-green-500 bg-green-50';
    case 'error':
      return 'border-red-500 bg-red-50';
    case 'warning':
      return 'border-yellow-500 bg-yellow-50';
    case 'info':
    default:
      return 'border-blue-500 bg-blue-50';
  }
};

const getIconClasses = (type: string) => {
  const baseClasses = 'w-5 h-5';
  switch (type) {
    case 'success':
      return `${baseClasses} text-green-500`;
    case 'error':
      return `${baseClasses} text-red-500`;
    case 'warning':
      return `${baseClasses} text-yellow-500`;
    case 'info':
    default:
      return `${baseClasses} text-blue-500`;
  }
};

const getTitleClasses = (type: string) => {
  switch (type) {
    case 'success':
      return 'text-green-800';
    case 'error':
      return 'text-red-800';
    case 'warning':
      return 'text-yellow-800';
    case 'info':
    default:
      return 'text-blue-800';
  }
};

const getMessageClasses = (type: string) => {
  switch (type) {
    case 'success':
      return 'text-green-700';
    case 'error':
      return 'text-red-700';
    case 'warning':
      return 'text-yellow-700';
    case 'info':
    default:
      return 'text-blue-700';
  }
};

// Simple icon components
const CheckCircleIcon = {
  template: `
    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
    </svg>
  `
};

const XCircleIcon = {
  template: `
    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
    </svg>
  `
};

const ExclamationTriangleIcon = {
  template: `
    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
    </svg>
  `
};

const InformationCircleIcon = {
  template: `
    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
    </svg>
  `
};
</script>

<style scoped>
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}
</style>